import React, { Key, useEffect, useMemo, useState } from "react";
import { Button, Form, Row, Tooltip } from "antd";
import { useHistory } from "react-router";
import { uniqueId } from "lodash";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import ToggleableVersionsTable from "../../../../components/ToggleableVersionsTable/ToggleableVersionsTable";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { TableColumn } from "../../../../lib/interfaces";
import { useAppDispatch, useAppSelector } from "../../../..";
import { checkIsLoading } from "../../../../lib/utils";
import { fetchPolicyVersions } from "../../store/slice/policiesSlice";
import { usePoliciesContext } from "../../context/PoliciesContext/PoliciesContext";
import "./PoliciesListTable.less";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { MENU_ADMIN_OFFERING_POLICY_WRITE } from "../../../../lib/const/permissions";

export const PoliciesListTable = () => {
  const history = useHistory();

  const dispatch = useAppDispatch();

  const { handlePoliciesListSearch } = usePoliciesContext();

  const {
    policiesList,
    policiesListPagination,
    policiesListLoading,
    policiesTypesDic,
    policiesActionsDic,
    policyVersions,
    policyVersionsLoading,
    policiesStatusDic,
  } = useAppSelector((state) => state.policies);

  const [form] = Form.useForm();

  

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  interface TableData {
    key?: Key;
    id?: number;
    name?: string;
    fromDate?: string;
    typeId?: number;
    actionId?: number;
    parameterOne?: string;
    parameterTwo?: string;
    statusId?: number;
    actions?: string;
    buttons?: string;
  }

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    name: "100px",
    typeId: "100px",
    actionId: "100px",
    parameterOne: "100px",
    parameterTwo: "100px",
    statusId: "100px",
  };

  const getColumns = (): TableColumn<TableData>[] => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      hidden: false,
    },
    {
      title: "Тип",
      dataIndex: "typeId",
      key: "typeId",
      width: columnsWidths.typeId,
      hidden: false,
      render: (typeId) => versionsTableUtils.getDictionaryItemById(policiesTypesDic, typeId)?.name,
    },
    {
      title: "Действие",
      dataIndex: "actionId",
      key: "actionId",
      width: columnsWidths.actionId,
      hidden: false,
      render: (actionId) =>
        versionsTableUtils.getDictionaryItemById(policiesActionsDic, actionId)?.name,
    },
    {
      title: "Параметр 1",
      dataIndex: "parameterOne",
      key: "parameterOne",
      width: columnsWidths.parameterOne,
      hidden: false,
    },
    {
      title: "Параметр 2",
      dataIndex: "parameterTwo",
      key: "parameterTwo",
      width: columnsWidths.parameterTwo,
      hidden: false,
    },
    {
      title: "Статус",
      dataIndex: "statusId",
      key: "statusId",
      width: columnsWidths.statusId,
      hidden: false,
      render: (statusId) =>
        versionsTableUtils.getDictionaryItemById(policiesStatusDic, statusId)?.name,
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      render: (value, record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showHistoryModal(Number(record?.id));
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ];

  const [columns, setColumns] = useState<TableColumn<TableData>[]>(getColumns());

  // Обновление колонок
  useEffect(() => {
    setColumns(getColumns());
  }, [policiesTypesDic, policiesActionsDic, policiesStatusDic]);

  const tableData = useMemo((): TableData[] => {
    const data: TableData[] = policiesList.map(
      ({
        name,
        typeId,
        id,
        actionId,
        assocOffering,
        assocOfferingGroup,
        actorOffering,
        actorOfferingGroup,
        status,
      }) => ({
        key: id,
        id,
        name,
        typeId,
        actionId,
        parameterOne: assocOffering?.name || assocOfferingGroup?.name,
        parameterTwo: actorOffering?.name || actorOfferingGroup?.name,
        statusId: status,
      })
    );
    return data;
  }, [policiesList, policiesTypesDic, policiesActionsDic]);

  const childTableData = useMemo((): TableData[] => {
    const data: TableData[] = policyVersions.map(
      ({
        name,
        typeId,
        id,
        actionId,
        assocOffering,
        assocOfferingGroup,
        actorOffering,
        actorOfferingGroup,
        status,
      }) => ({
        key: `id_${id}_unicId_${uniqueId()}`,
        id,
        name,
        typeId,
        actionId,
        parameterOne: assocOffering?.name || assocOfferingGroup?.name,
        parameterTwo: actorOffering?.name || actorOfferingGroup?.name,
        statusId: status,
      })
    );
    return data;
  }, [policyVersions, policiesTypesDic, policiesActionsDic]);

  // console.log({ tableData });

  const onOpenVersion = (record: TableData, expanded: boolean) => {
    dispatch(fetchPolicyVersions(record.id!));
  };

  const onFinish = () => {};

  return (
    <div className="">
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          tableName="policies-list-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={columns}
          // setChildTableColumns={setChildTableColumns}
          childTableData={childTableData}
          isTableLoading={checkIsLoading(policiesListLoading)}
          isChildTableLoading={checkIsLoading(policyVersionsLoading)}
          onOpenVersionsCallback={onOpenVersion}
          onRow={(record) => ({
            onClick: () => {
              history.push(`/policies/${record.id}`);
            },
          })}
          isPageable={{
            current: policiesListPagination.current + 1,
            total: policiesListPagination.total,
            pageSize: 7,
            showSizeChanger: false,
            onChange: (page: number) => {
              handlePoliciesListSearch({ page: page - 1 });
            },
          }}
        />
      </Form>

      <HistoryAndPropertyModal
        objectTypeSysname="offering_policy"
        objectId={objectId}
        handleHideModal={() => {
          setHistoryModalVisible(false);
        }}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_OFFERING_POLICY_WRITE)}
      />
    </div>
  );
};
